import { FormData, MonthlyIncomeData, MonthData } from "../Models/disability-calculator";
import {
  formatCurrency,
  toNumber,
  getYearValueOrDefault,
  parsePercentage,
  parseUTCDate,
} from "../utils/utils";
import { MONTH_NAMES } from "../constants/constants";
import { generateUnifiedSickVacationTable } from "../utils/unifiedTable";
import { UnifiedTableRow } from "../Models/disability-calculator";

export function getMonthlyIncomeTableData(formData: FormData): MonthlyIncomeData[] {
  const { employmentInfo, yearRange } = formData;
  const disabilityDate = employmentInfo.dateOfDisability;

  if (!disabilityDate) {
    return [];
  }

  const disabilityDateObj = parseUTCDate(disabilityDate);

  if (yearRange && isSpecificYearRangeSelected(yearRange, disabilityDateObj)) {
    const monthsArray = generateMonthsForYearRange(
      disabilityDateObj,
      yearRange,
      employmentInfo.dateOfRetirement
    );
    return generateMonthlyDataWithYears(formData, monthsArray);
  } else {
    const monthsArray = generateMonthsFromDisability(disabilityDateObj);
    return generateMonthlyData(formData, monthsArray);
  }
}

function isSpecificYearRangeSelected(
  yearRange: string,
  disabilityDate: Date
): boolean {
  if (!yearRange || !yearRange.includes(" - ")) {
    return false;
  }

  const [startYear] = yearRange.split(" - ").map((y) => parseInt(y.trim()));
  const disabilityYear = disabilityDate.getUTCFullYear();

  return startYear >= disabilityYear;
}

function generateMonthsForYearRange(
  disabilityDate: Date,
  yearRange: string,
  retirementDate: string
): MonthData[] {
  const [startYear, endYear] = yearRange
    .split(" - ")
    .map((y) => parseInt(y.trim()));
  const retirementDateObj = parseUTCDate(retirementDate);
  const months: MonthData[] = [];

  for (let year = startYear; year <= endYear; year++) {
    for (let month = 0; month < 12; month++) {
      const currentDate = new Date(Date.UTC(year, month, 1));
      const disabilityYear = disabilityDate.getUTCFullYear();
      const disabilityMonth = disabilityDate.getUTCMonth();
      const isAfterOrSameMonth = 
        (year > disabilityYear) || 
        (year === disabilityYear && month >= disabilityMonth);
      
      
      if (isAfterOrSameMonth && currentDate <= retirementDateObj) {
        months.push({
          month: MONTH_NAMES[month],
          year: year,
          fullDate: currentDate,
        });
      }
    }
  }

  return months;
}

function findChronologicalMonthIndex(
  formData: FormData,
  actualYear: number,
  monthDate: Date,
  sickBankTable: UnifiedTableRow[]
): number {
  const targetMonth = monthDate.getUTCMonth(); // 0-11

  // Search through sick bank table to find matching year and month
  for (let i = 0; i < sickBankTable.length; i++) {
    const tableEntry = sickBankTable[i];

    // Check if year and month match
    if (tableEntry.year === actualYear) {
      const MONTH_NAMES = [
        "JAN",
        "FEB",
        "MAR",
        "APR",
        "MAY",
        "JUN",
        "JUL",
        "AUG",
        "SEP",
        "OCT",
        "NOV",
        "DEC",
      ];
      const tableMonthIndex = MONTH_NAMES.indexOf(tableEntry.month);

      if (tableMonthIndex === targetMonth) {
        return i; // Found matching month/year
      }
    }
  }

  return -1; // Not found
}


function generateMonthlyDataWithYears(
  formData: FormData,
  monthsArray: MonthData[]
): MonthlyIncomeData[] {
  const monthlyData: MonthlyIncomeData[] = [];

  // Get the unified table (cached)
  const unifiedTable = generateUnifiedSickVacationTable(formData);

  for (let i = 0; i < monthsArray.length; i++) {
    const monthObj = monthsArray[i];
    const month = monthObj.month;
    const actualYear = monthObj.year;

    // Find the chronological index in the unified table (like calculateSickPayWithYear does)
    const chronologicalIndex = findChronologicalMonthIndex(
      formData,
      actualYear,
      monthObj.fullDate,
      unifiedTable
    );
    

    // Get unified table row using chronological index
    const unifiedRow: UnifiedTableRow | null =
      chronologicalIndex !== -1 ? unifiedTable[chronologicalIndex] : null;

    // Get LOL benefit from unified table instead of recalculating
    const lolBenefit = unifiedRow ? unifiedRow.lolBenefit : 0;

    // Get sick pay from unified table instead of recalculating
    const sickPay = unifiedRow ? unifiedRow.sickPay : 0;

    // Get STD from unified table instead of recalculating
    const std = unifiedRow ? unifiedRow.std : 0;

    // Get LTD from unified table instead of recalculating
    const ltdValue = unifiedRow ? unifiedRow.ltdValue : 0;

    // Get vacation pay from unified table instead of recalculating
    const vacationPay = unifiedRow ? unifiedRow.vacationPay : 0;

    // Get lolNecBase from unified table
    const lolNecBase = unifiedRow ? unifiedRow.lolNecBase : 0;

    // Cash Award logic:
    // If LOL is non-taxable (lolTaxable === 'No'): Use LOL NEC BASE (BG54)
    // If LOL is taxable (lolTaxable === 'Yes'): Use 0
    const lolTaxable = formData.financialInfo.lolTaxable;
    const cashAward =
      lolTaxable.toLowerCase() === "non-taxable" ? lolNecBase : 0;

    // Calculate Taxable Wages
    // If LOL is Taxable: Include LOL Benefit + Sick Pay + Vacation Pay
    // If LOL is Nontaxable: Include only Sick Pay + Vacation Pay (exclude LOL Benefit)
    const taxableWages =
      lolTaxable.toLowerCase() === "taxable"
        ? lolBenefit + sickPay + vacationPay // LOL Taxable: include LOL benefit
        : sickPay + vacationPay + cashAward; // LOL Nontaxable: exclude LOL benefit

    // Calculate Est Post Tax Wages
    // Formula: Taxable Wages × (1 - (Federal + FICA + State))
    const federalTax = parsePercentage(formData.financialInfo.federalTaxBracket); // User input
    const pretax = parsePercentage(formData.financialInfo.preTax); // User input

    // Calculate FICA from CMS values: SSPect + MED
    const ssPect = formData.assumptions?.SSPect ? parseFloat(formData.assumptions.SSPect) / 100 : 0; // Default 0 if not in CMS
    const med = formData.assumptions?.MED ? parseFloat(formData.assumptions.MED) / 100 : 0; // Default 0 if not in CMS
    const ficaTax = ssPect + med; // Dynamic FICA calculation from CMS
    
    const stateTax =
      parsePercentage(formData.financialInfo.stateTaxBracket); // User input
    const totalTaxRate = federalTax + ficaTax + stateTax + pretax;
    const postTaxWages = taxableWages * (1 - totalTaxRate);

    // Determine LOL placement based on taxability
    const lolTaxableBenefit =
      lolTaxable.toLowerCase() === "taxable" ? lolBenefit : 0;
    const lolNontaxableBenefit =
      lolTaxable.toLowerCase() === "non-taxable" ? lolBenefit : 0;

    // Calculate TOTAL AFTER TAX [NET-NET]
    // Formula: Est Post Tax Wages + STD + LOL (Nontaxable) + LTD
    // Note: ltdValue already contains either Plan A or Plan B value based on user's ltdPlan selection
    const totalAfterTax = postTaxWages + std + lolNontaxableBenefit + ltdValue;

    monthlyData.push({
      month: `${month}`, // Show year only if multiple years
      lolTaxableBenefit:
        lolTaxableBenefit > 0 ? formatCurrency(lolTaxableBenefit) : "-",
      lolNontaxable:
        lolNontaxableBenefit > 0 ? formatCurrency(lolNontaxableBenefit) : "-",
      sickPay: formatCurrency(sickPay),
      vacationPay: formatCurrency(vacationPay),
      cashAward: formatCurrency(cashAward),
      taxableWages: formatCurrency(taxableWages),
      postTaxWages: formatCurrency(postTaxWages),
      std:
        formData.financialInfo.stdPlan === "Yes" ||
        formData.financialInfo.stdPlan === "yes"
          ? formatCurrency(std)
          : "-",
      ltd: formatCurrency(ltdValue),
      totalAfterTax: formatCurrency(totalAfterTax),
      year: actualYear,
    });
  }

  return monthlyData;
}

function generateMonthsFromDisability(disabilityDate: Date): string[] {
  const startMonth = disabilityDate.getUTCMonth();
  const months: string[] = [];

  for (let i = 0; i < 12; i++) {
    const monthIndex = (startMonth + i) % 12;
    months.push(MONTH_NAMES[monthIndex]);
  }

  return months;
}

function generateMonthlyData(formData: FormData, monthsArray: string[]): MonthlyIncomeData[] {
  // Convert simple month strings to the same object format that generateMonthlyDataWithYears expects
  const { employmentInfo } = formData;
  const disabilityDate = employmentInfo.dateOfDisability;
  const disabilityDateObj = parseUTCDate(disabilityDate);
  const disabilityYear = disabilityDateObj.getUTCFullYear();
  const disabilityMonth = disabilityDateObj.getUTCMonth(); // 0-based month index


  // Convert string array to object array like generateMonthsForYearRange produces
  const monthObjectsArray: MonthData[] = monthsArray.map((month, i) => {
    const currentMonthIndex = MONTH_NAMES.indexOf(month);

    // Calculate the actual year for this month (same logic as before)
    const monthsFromStart = i;
    const totalMonthsFromDisability = disabilityMonth + monthsFromStart;
    const actualYear =
      disabilityYear + Math.floor(totalMonthsFromDisability / 12);

    // Create fullDate using UTC like generateMonthsForYearRange does
    const fullDate = new Date(Date.UTC(actualYear, currentMonthIndex, 1));

    return {
      month: month,
      year: actualYear,
      fullDate: fullDate,
    };
  });

  // Now call generateMonthlyDataWithYears with the properly formatted array
  return generateMonthlyDataWithYears(formData, monthObjectsArray);
}
